import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import { format, subDays, subWeeks, subMonths, subYears, startOfDay, endOfDay, parseISO, isWithinInterval, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import imageCompression from 'browser-image-compression';
import ExpenseDetailsModal from '@/components/Expense/ExpenseDetailsModal';
import ExpensesBarChart from '@/components/Charts/ExpensesBarChart';
import 'chartjs-adapter-date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Modal from '@/components/common/Modal';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title
);

interface Provider {
  id: string;
  name: string;
}

interface Expense {
  id: string;
  amount: number;
  provider_id: string;
  provider: Provider;
  date: string;
  document_url: string | null;
}

interface ChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
    borderColor: string[];
    borderWidth: number;
  }[];
}

interface ExpenseDetailsModalProps {
  expense: Expense | null;
  onClose: () => void;
}

interface TimeFrameOption {
  label: string;
  value: 'day' | 'week' | 'month' | 'year';
  getStartDate: () => Date;
}

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
    color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827',
    borderRadius: '0.5rem',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? '#334155' : '#e2e8f0',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0ea5e9',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0ea5e9',
    },
  },
}));

export default function ExpensesPage() {
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [displayedExpenses, setDisplayedExpenses] = useState<Expense[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeFrame, setTimeFrame] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const { addToast } = useToast();

  // Form state
  const [amount, setAmount] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [file, setFile] = useState<File | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  
  const [numItemsShown, setNumItemsShown] = useState(0);
  const [hasMoreToDisplay, setHasMoreToDisplay] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const chartColors = [
    'rgba(255, 99, 132, 0.8)',  // Red
    'rgba(54, 162, 235, 0.8)', // Blue
    'rgba(255, 206, 86, 0.8)', // Yellow
    'rgba(75, 192, 192, 0.8)', // Teal
    'rgba(153, 102, 255, 0.8)', // Purple
    'rgba(255, 159, 64, 0.8)', // Orange
    'rgba(231, 76, 60, 0.8)',  // Alizarin
    'rgba(46, 204, 113, 0.8)', // Emerald
    'rgba(52, 152, 219, 0.8)', // Peter River
    'rgba(142, 68, 173, 0.8)', // Amethyst
    'rgba(241, 196, 15, 0.8)', // Sun Flower
    'rgba(230, 126, 34, 0.8)', // Carrot
    'rgba(26, 188, 156, 0.8)', // Turquoise
    'rgba(41, 128, 185, 0.8)', // Belize Hole
    'rgba(243, 156, 18, 0.8)', // Orange (different)
    'rgba(192, 57, 43, 0.8)',  // Pomegranate
  ];

  const timeFrameOptions: TimeFrameOption[] = [
    {
      label: 'Day',
      value: 'day',
      getStartDate: () => subDays(new Date(), 1),
    },
    {
      label: 'Week',
      value: 'week',
      getStartDate: () => subWeeks(new Date(), 1),
    },
    {
      label: 'Month',
      value: 'month',
      getStartDate: () => subMonths(new Date(), 1),
    },
    {
      label: 'Year',
      value: 'year',
      getStartDate: () => subYears(new Date(), 1),
    },
  ];

  const [currentDate, setCurrentDate] = useState(new Date());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchProviders();
    fetchAllExpensesAndSetInitialDisplay();
  }, []);

  const fetchProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setProviders(data || []);
    } catch (error) {
      addToast({
        type: 'error',
        message: 'Failed to fetch providers',
      });
    }
  };

  const fetchAllExpensesAndSetInitialDisplay = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('expenses')
        .select(`
          *,
          provider:providers(id, name)
        `)
        .order('date', { ascending: false });

      if (error) throw error;
      const fetchedExpenses = data || [];
      setAllExpenses(fetchedExpenses);

      const initialDisplayCount = Math.min(fetchedExpenses.length, ITEMS_PER_PAGE);
      setDisplayedExpenses(fetchedExpenses.slice(0, initialDisplayCount));
      setNumItemsShown(initialDisplayCount);
      setHasMoreToDisplay(fetchedExpenses.length > initialDisplayCount);

    } catch (error) {
      addToast({
        type: 'error',
        message: 'Failed to fetch expenses',
      });
      setAllExpenses([]);
      setDisplayedExpenses([]);
      setNumItemsShown(0);
      setHasMoreToDisplay(false);
    } finally {
      setLoading(false);
    }
  };

  const compressImage = async (file: File): Promise<File> => {
    // Only compress if it's an image
    if (!file.type.startsWith('image/')) {
      console.log('📄 File is not an image, skipping compression:', {
        name: file.name,
        type: file.type,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`
      });
      return file;
    }

    console.log('🖼️ Starting image compression for:', {
      name: file.name,
      type: file.type,
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`
    });

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: file.type as string,
      initialQuality: 0.8,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      
      const compressionResult = {
        name: file.name,
        originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        compressedSize: `${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
        savedSpace: `${((file.size - compressedFile.size) / 1024 / 1024).toFixed(2)}MB`,
        compressionRatio: `${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`
      };
      
      // If compression didn't help (rare case), return original file
      if (compressedFile.size > file.size) {
        console.log('⚠️ Compressed file is larger than original, using original file:', compressionResult);
        return file;
      }

      console.log('✅ Image compression successful:', compressionResult);

      // Create a new file with the original name but compressed data
      return new File([compressedFile], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
    } catch (error) {
      console.warn('❌ Image compression failed, using original file:', {
        name: file.name,
        error,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`
      });
      return file;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let documentUrl: string | null = null;

      if (file) {
        const processedFile = await compressImage(file);
        const fileExt = processedFile.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `expenses/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('nestor')
          .upload(filePath, processedFile);

        if (uploadError) throw uploadError;
        documentUrl = `https://wnpxoafadiijibqqpblu.supabase.co/storage/v1/s3/nestor/${filePath}`;
      }

      const expenseData = {
        amount: parseFloat(amount),
        provider_id: selectedProvider,
        date: new Date(date).toISOString(),
        document_url: documentUrl,
      };

      if (editingExpense) {
        const { error } = await supabase
          .from('expenses')
          .update(expenseData)
          .eq('id', editingExpense.id);

        if (error) throw error;
        addToast({ type: 'success', message: 'Expense updated successfully' });
      } else {
        const { error } = await supabase
          .from('expenses')
          .insert([expenseData]);

        if (error) throw error;
        addToast({ type: 'success', message: 'Expense added successfully' });
      }

      setAmount('');
      setSelectedProvider('');
      setDate(format(new Date(), 'yyyy-MM-dd'));
      setFile(null);
      setEditingExpense(null);
      fetchAllExpensesAndSetInitialDisplay();
    } catch (error) {
      addToast({ type: 'error', message: 'Failed to save expense' });
    }
  };

  const handleDelete = async (id: string) => {
    setExpenseToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;

    try {
      const { data: expenseData } = await supabase
        .from('expenses')
        .select('document_url')
        .eq('id', expenseToDelete)
        .single();

      if (expenseData?.document_url) {
        const fileName = expenseData.document_url.split('/').pop();
        const { error: storageError } = await supabase.storage
          .from('nestor')
          .remove([`expenses/${fileName}`]);
        if (storageError) throw storageError;
      }

      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expenseToDelete);

      if (error) throw error;
      
      addToast({ type: 'success', message: 'Expense deleted successfully' });
      fetchAllExpensesAndSetInitialDisplay();
    } catch (error) {
      addToast({ type: 'error', message: 'Failed to delete expense' });
    } finally {
      setIsDeleteModalOpen(false);
      setExpenseToDelete(null);
    }
  };

  const getDateRange = () => {
    switch (timeFrame) {
      case 'day':
        return {
          start: startOfDay(currentDate),
          end: endOfDay(currentDate),
        };
      case 'week':
        return {
          start: startOfWeek(currentDate, { weekStartsOn: 1 }),
          end: endOfWeek(currentDate, { weekStartsOn: 1 }),
        };
      case 'month':
        return {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate),
        };
      case 'year':
        return {
          start: startOfYear(currentDate),
          end: endOfYear(currentDate),
        };
    }
  };

  const calculateTotalExpensesForTimeframe = () => {
    const { start, end } = getDateRange();
    return allExpenses
      .filter(expense => {
        const expenseDate = parseISO(expense.date);
        return isWithinInterval(expenseDate, { start, end });
      })
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  // Function to get colors based on data length
  const getDynamicChartColors = (count: number): string[] => {
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      colors.push(chartColors[i % chartColors.length]);
    }
    return colors;
  };

  const prepareChartData = (): ChartData => {
    const { start, end } = getDateRange();

    const filteredExpenses = allExpenses.filter(expense => {
      const expenseDate = parseISO(expense.date);
      return isWithinInterval(expenseDate, { start, end });
    });

    // Group filtered expenses by provider
    const groupedData: { [key: string]: number } = {};
    filteredExpenses.forEach((expense) => {
      const key = expense.provider.name;
      groupedData[key] = (groupedData[key] || 0) + expense.amount;
    });

    const labels = Object.keys(groupedData);
    const dataValues = Object.values(groupedData);
    const backgroundColors = getDynamicChartColors(labels.length);

    return {
      labels: labels,
      datasets: [
        {
          data: dataValues,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors.map(color => color.replace('0.8', '1')),
          borderWidth: 1,
        },
      ],
    };
  };

  const prepareLineChartData = () => {
    const startDate = timeFrameOptions.find(opt => opt.value === timeFrame)?.getStartDate() || new Date();
    const endDate = new Date();
    
    const filteredExpenses = allExpenses.filter(expense => {
      const expenseDate = parseISO(expense.date);
      return isWithinInterval(expenseDate, { start: startOfDay(startDate), end: endOfDay(endDate) });
    });

    // Group expenses by date
    const groupedData = filteredExpenses.reduce((acc: { [key: string]: number }, expense) => {
      const date = format(parseISO(expense.date), 'yyyy-MM-dd');
      acc[date] = (acc[date] || 0) + expense.amount;
      return acc;
    }, {});

    // Sort dates and fill in missing dates with zero
    const dates = Object.keys(groupedData).sort();
    const data = dates.map(date => ({
      x: parseISO(date),
      y: groupedData[date],
    }));

    return {
      datasets: [
        {
          label: 'Total Expenses',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.3,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `€${value.toFixed(2)} (${percentage}%)`;
          },
        },
      },
    },
    layout: {
      padding: 20,
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `€${context.raw.y.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: (timeFrame === 'day' ? 'hour' : 
                timeFrame === 'week' ? 'day' : 
                timeFrame === 'month' ? 'day' : 'month') as 'hour' | 'day' | 'month',
        },
        title: {
          display: true,
          text: 'Date',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Amount (€)',
        },
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  const handleLoadMore = () => {
    if (!hasMoreToDisplay || loading) return;

    const newNumItemsToDisplay = Math.min(allExpenses.length, numItemsShown + ITEMS_PER_PAGE);
    setDisplayedExpenses(allExpenses.slice(0, newNumItemsToDisplay));
    setNumItemsShown(newNumItemsToDisplay);
    setHasMoreToDisplay(allExpenses.length > newNumItemsToDisplay);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const formatDate = (date: Date, timeframe: string) => {
    switch (timeframe) {
      case 'day':
        return format(date, 'MMM d, yyyy');
      case 'week':
        return `Week of ${format(date, 'MMM d, yyyy')}`;
      case 'month':
        return format(date, 'MMM, yyyy');
      case 'year':
        return format(date, 'yyyy');
      default:
        return format(date, 'MMM d, yyyy');
    }
  };

  const handlePrevious = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() - 1);
      return newDate;
    });
  };

  const handleNext = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + 1);
      return newDate;
    });
  };

  return (
    <div className="container mx-auto px-4 pt-4">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Expenses Management</h1>
        
        {/* Add/Edit Expense Form */}
        <div className="bg-white dark:bg-dark-bg-light rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-lg font-medium mb-6 text-gray-900 dark:text-white">
            {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-dark-text-secondary">
                    €
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full h-11 pl-7 pr-4 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg-lighter dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Provider
                </label>
                <select
                  required
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg-lighter dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value="">Select a provider</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Date
                </label>
                <StyledDatePicker
                  value={new Date(date)}
                  onChange={(value) => {
                    const dateValue = value as Date | null;
                    if (dateValue) setDate(format(dateValue, 'yyyy-MM-dd'));
                  }}
                  format="MM/dd/yyyy"
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                    desktopPaper: {
                      sx: {
                        backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
                        '.MuiPickersDay-root': {
                          color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827',
                        },
                      },
                    },
                  }}
                  orientation={isMobile ? "portrait" : "landscape"}
                  views={['year', 'month', 'day']}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Document
                </label>
                <div className="relative">
                  <input
                    type="file"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="document-upload"
                    accept=".pdf,.png,.jpg,.jpeg"
                  />
                  <label
                    htmlFor="document-upload"
                    className="w-full h-11 flex items-center px-4 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-lighter hover:bg-gray-50 dark:hover:bg-dark-bg-lightest cursor-pointer transition-colors duration-200 group"
                  >
                    <i className="fas fa-upload mr-2 text-gray-400 group-hover:text-primary-500 transition-colors duration-200"></i>
                    <span className="text-gray-500 dark:text-dark-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
                      {file ? file.name : 'Upload Document'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              {editingExpense && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingExpense(null);
                    setAmount('');
                    setSelectedProvider('');
                    setDate(format(new Date(), 'yyyy-MM-dd'));
                    setFile(null);
                  }}
                  className="btn btn-outline btn-outline-secondary"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Saving...' : editingExpense ? 'Update Expense' : 'Add Expense'}
              </button>
            </div>
          </form>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Pie Chart */}
          <div className="bg-white dark:bg-dark-bg-light rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Expenses by Provider</h2>
            </div>
            <div className="h-[400px] relative">
              {(() => {
                const { start, end } = getDateRange();
                const relevantExpensesForChartCheck = allExpenses.filter(expense => {
                  const expenseDate = parseISO(expense.date);
                  return isWithinInterval(expenseDate, { start, end });
                });

                if (relevantExpensesForChartCheck.length === 0) {
                  return (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <i className="fas fa-chart-pie text-4xl text-gray-400 dark:text-dark-text-secondary mb-3"></i>
                        <p className="text-gray-500 dark:text-dark-text-secondary">No expenses found for this time frame</p>
                      </div>
                    </div>
                  );
                }

                const chartData = prepareChartData();
                return <Pie data={chartData} options={chartOptions} />;
              })()}
            </div>
            <div className="mt-4 text-center">
              <div className="text-sm text-gray-600 dark:text-dark-text-secondary">Total Expenses for Selected Period</div>
              <div className="text-xl font-semibold text-purple-600 dark:text-purple-400">
                €{calculateTotalExpensesForTimeframe().toFixed(2)}
              </div>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="bg-white dark:bg-dark-bg-light rounded-xl shadow-lg p-6">
            <ExpensesBarChart
              expenses={allExpenses}
              timeFrame={timeFrame}
              onTimeFrameChange={setTimeFrame}
              onDateChange={setCurrentDate}
            />
          </div>
        </div>

        {/* Expenses List */}
        <div className="bg-white dark:bg-dark-bg-light rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Expenses</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
              <thead className="bg-gray-50 dark:bg-dark-bg-lighter">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Document</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-bg-light divide-y divide-gray-200 dark:divide-dark-border">
                {displayedExpenses.map((expense) => (
                  <tr 
                    key={expense.id} 
                    className="hover:bg-gray-50 dark:hover:bg-dark-bg-lighter cursor-pointer transition-colors duration-200"
                    onClick={() => setSelectedExpense(expense)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {format(new Date(expense.date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {expense.provider.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      €{expense.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {expense.document_url && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedExpense(expense);
                          }}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          <i className="fas fa-file-alt" />
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingExpense(expense);
                            setAmount(expense.amount.toString());
                            setSelectedProvider(expense.provider_id);
                            setDate(format(new Date(expense.date), 'yyyy-MM-dd'));
                            scrollToTop();
                          }}
                          className="btn btn-icon btn-primary"
                        >
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(expense.id);
                          }}
                          className="btn btn-icon btn-danger"
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMoreToDisplay && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="w-full py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Expense Details Modal */}
      {selectedExpense && (
        <ExpenseDetailsModal
          expense={selectedExpense}
          onClose={() => setSelectedExpense(null)}
          onDocumentDelete={() => {
            setSelectedExpense(null);
            fetchAllExpensesAndSetInitialDisplay();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setExpenseToDelete(null);
        }}
        title="Delete Expense"
        actions={
          <>
            <button
              onClick={() => {
                setIsDeleteModalOpen(false);
                setExpenseToDelete(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Are you sure you want to delete this expense? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
} 